// ./RegistrationFields/CompanyManagementType/CompanyManagementType.jsx

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import {
  FormAutosuggest,
  FormAutosuggestOption,
  FormControlFeedback,
} from '@openedx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { clearRegistrationBackendError } from '../../data/actions';
import messages from '../../messages';

const EDUCATION_VALUE_KEY = 'value';
const EDUCATION_LABEL_KEY = 'label';

const DEFAULT_EDUCATION_TYPE_LIST = [
  { value: 'p', label: 'Doctorate' },
  { value: 'm', label: "Master's or professional degree" },
  { value: 'b', label: "Bachelor's degree" },
  { value: 'a', label: "Associate degree" },
  { value: 'hs', label: "Secondary/high school" },
  { value: 'jhs', label: "Junior secondary/junior high/middle school" },
  { value: 'el', label: "Elementary/primary school" },
  { value: 'none', label: "No formal education" },
  { value: 'other', label: "Other education" }
];

const EducationType = (props) => {
  const {
    name,
    value,
    handleChange,
    handleErrorChange,
    errorMessage,
    floatingLabel,
    educationTypeList,
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const educationTypes = educationTypeList || DEFAULT_EDUCATION_TYPE_LIST;
  const [inputText, setInputText] = useState('');

  const selected = educationTypes.find(
    item => item[EDUCATION_VALUE_KEY] === value,
  ) || { value: '', label: '' };

  useEffect(() => {
    if (value) {
      setInputText(selected[EDUCATION_LABEL_KEY] || '');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldValue = {
    userProvidedText: inputText,
    selectionValue: selected[EDUCATION_LABEL_KEY] || '',
    selectionId: selected[EDUCATION_VALUE_KEY] || '',
  };

  const runValidation = () => {
    handleErrorChange(name, '');
  };

  const handleOnFocus = () => {
    dispatch(clearRegistrationBackendError(name));
    handleErrorChange(name, '');
  };

  const handleOnBlur = () => {
    runValidation();
  };

  const handleOnChangeAutosuggest = (autosuggestValue) => {
    const code = autosuggestValue.selectionId || '';
    setInputText(autosuggestValue.userProvidedText || autosuggestValue.selectionValue || '');

    handleChange({
      target: {
        name,
        value: code,
      },
    });

    runValidation();
  };

  const getOptions = () => educationTypes.map(option => (
    <FormAutosuggestOption
      key={option[EDUCATION_VALUE_KEY]}
      id={option[EDUCATION_VALUE_KEY]}
    >
      {option[EDUCATION_LABEL_KEY]}
    </FormAutosuggestOption>
  ));
  const hasMatchingOptions = !inputText.trim()
    || educationTypes.some(option => option[EDUCATION_LABEL_KEY].toLowerCase().includes(inputText.toLowerCase()));

  return (
    <div className="mb-4">
      <FormAutosuggest
        floatingLabel={
          floatingLabel
          || formatMessage(messages['registration.level_of_education.label'])
        }
        aria-label="education type"
        name={name}
        value={fieldValue || {}}
        className={classNames({ 'form-field-error': errorMessage })}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={handleOnChangeAutosuggest}
      >
        {getOptions()}
      </FormAutosuggest>
      {errorMessage && (
        <FormControlFeedback
          key="error"
          className="form-text-size"
          hasIcon={false}
          feedback-for={name}
          type="invalid"
        >
          {errorMessage}
        </FormControlFeedback>
      )}
      {!errorMessage && !hasMatchingOptions && (
        <div className="autosuggest-no-results">{formatMessage(messages['registration.no.results.found'])}</div>
      )}
    </div>
  );
};

EducationType.propTypes = {
  name: PropTypes.string.isRequired,          // e.g. "educationType"
  value: PropTypes.string.isRequired,         // e.g. "Doctorate"
  handleChange: PropTypes.func.isRequired,
  handleErrorChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  floatingLabel: PropTypes.string,
  educationTypeList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
};

EducationType.defaultProps = {
  errorMessage: '',
  floatingLabel: '',
  educationTypeList: null,
};

export default EducationType;
